import { FormEvent, useEffect, useState, type DragEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { WarrantyType } from '@fixflow/shared-types';
import { formatWarrantySummary } from '@fixflow/shared-types';
import {
  applyWarrantyRulesToPricing,
  createWarrantyRule,
  deleteWarrantyRule,
  listWarrantyRules,
  reorderWarrantyRules,
  testWarrantyRule,
  updateWarrantyRule,
  type WarrantyRule,
  type WarrantyRuleInput,
} from '@/lib/api/warranty-rules.js';
import { listDeviceMakes } from '@/lib/api/devices.js';
import { WarrantyField } from '@/components/common/WarrantyField.js';
import { parseApiError } from '@/lib/api/errors.js';
import { toastError, toastSuccess } from '@/lib/toast.js';
import { cn } from '@/lib/utils.js';

const DEVICE_CATEGORIES = [
  { value: '', label: 'Any category' },
  { value: 'phone', label: 'Phone' },
  { value: 'tablet', label: 'Tablet' },
  { value: 'laptop', label: 'Laptop' },
  { value: 'console', label: 'Console' },
  { value: 'watch', label: 'Watch' },
  { value: 'other', label: 'Other' },
];

const emptyForm: WarrantyRuleInput = {
  ruleDescription: '',
  serviceNamePattern: '',
  deviceMakeId: null,
  deviceCategory: null,
  warrantyType: 'days',
  warrantyDays: 90,
  active: true,
};

export function WarrantyRulesPage() {
  const queryClient = useQueryClient();
  const rulesQuery = useQuery({ queryKey: ['warranty-rules'], queryFn: () => listWarrantyRules() });
  const makesQuery = useQuery({ queryKey: ['device-makes'], queryFn: listDeviceMakes });

  const [rules, setRules] = useState<WarrantyRule[]>([]);
  const [editing, setEditing] = useState<WarrantyRule | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<WarrantyRuleInput>(emptyForm);
  const [dragIndex, setDragIndex] = useState<number | null>(null);

  const [testServiceName, setTestServiceName] = useState('Screen Replacement');
  const [testMakeId, setTestMakeId] = useState('');
  const [testCategory, setTestCategory] = useState('phone');
  const [testResult, setTestResult] = useState<string | null>(null);

  const [applyPreview, setApplyPreview] = useState<Awaited<
    ReturnType<typeof applyWarrantyRulesToPricing>
  > | null>(null);
  const [showApply, setShowApply] = useState(false);

  useEffect(() => {
    if (rulesQuery.data) setRules(rulesQuery.data);
  }, [rulesQuery.data]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload: WarrantyRuleInput = {
        warrantyType: form.warrantyType ?? 'days',
        warrantyDays: form.warrantyType === 'days' ? form.warrantyDays ?? 90 : 0,
        active: form.active ?? true,
      };
      if (form.ruleDescription?.trim()) payload.ruleDescription = form.ruleDescription.trim();
      payload.serviceNamePattern = form.serviceNamePattern?.trim() || null;
      payload.deviceMakeId = form.deviceMakeId || null;
      payload.deviceCategory = form.deviceCategory || null;
      if (form.serviceId) payload.serviceId = form.serviceId;
      if (editing) return updateWarrantyRule(editing.id, payload);
      return createWarrantyRule(payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['warranty-rules'] });
      setShowForm(false);
      setEditing(null);
      setForm(emptyForm);
      toastSuccess(editing ? 'Rule updated' : 'Rule created');
    },
    onError: (error) => toastError(parseApiError(error).message),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteWarrantyRule(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['warranty-rules'] });
      toastSuccess('Rule deleted');
    },
    onError: (error) => toastError(parseApiError(error).message),
  });

  const reorderMutation = useMutation({
    mutationFn: (orderedIds: string[]) => reorderWarrantyRules(orderedIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['warranty-rules'] });
    },
    onError: (error) => toastError(parseApiError(error).message),
  });

  const testMutation = useMutation({
    mutationFn: () => {
      const body: {
        serviceName?: string;
        makeId?: string;
        deviceCategory?: string;
      } = { serviceName: testServiceName };
      if (testMakeId) body.makeId = testMakeId;
      if (testCategory) body.deviceCategory = testCategory;
      return testWarrantyRule(body);
    },
    onSuccess: (data) => {
      const label =
        data.label ??
        formatWarrantySummary({ type: data.warrantyType, days: data.warrantyDays });
      setTestResult(
        data.matched
          ? `Rule #${data.priority ?? '—'} matches: ${data.ruleDescription} → ${label}`
          : `No rule matched — ${label} (${data.source ?? 'fallback'})`,
      );
    },
    onError: (error) => toastError(parseApiError(error).message),
  });

  const applyDryRunMutation = useMutation({
    mutationFn: () => applyWarrantyRulesToPricing(true),
    onSuccess: (data) => {
      setApplyPreview(data);
      setShowApply(true);
    },
    onError: (error) => toastError(parseApiError(error).message),
  });

  const applyMutation = useMutation({
    mutationFn: () => applyWarrantyRulesToPricing(false),
    onSuccess: (data) => {
      toastSuccess(`Updated ${data.updated} pricing rows`);
      setShowApply(false);
      setApplyPreview(null);
    },
    onError: (error) => toastError(parseApiError(error).message),
  });

  function openCreate() {
    setEditing(null);
    setForm(emptyForm);
    setShowForm(true);
  }

  function openEdit(rule: WarrantyRule) {
    setEditing(rule);
    setForm({
      ruleDescription: rule.ruleDescription ?? '',
      serviceNamePattern: rule.serviceNamePattern ?? '',
      deviceMakeId: rule.deviceMakeId,
      deviceCategory: rule.deviceCategory,
      warrantyType: rule.warrantyType ?? 'days',
      warrantyDays: rule.warrantyDays,
      active: rule.active,
      serviceId: rule.serviceId,
    });
    setShowForm(true);
  }

  function handleDragStart(index: number) {
    setDragIndex(index);
  }

  function handleDragOver(e: DragEvent, index: number) {
    e.preventDefault();
    if (dragIndex == null || dragIndex === index) return;
    setRules((current) => {
      const next = [...current];
      const [moved] = next.splice(dragIndex, 1);
      if (!moved) return current;
      next.splice(index, 0, moved);
      return next;
    });
    setDragIndex(index);
  }

  function handleDragEnd() {
    setDragIndex(null);
    void reorderMutation.mutateAsync(rules.map((r) => r.id));
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    saveMutation.mutate();
  }

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-slate-950">Warranty Rules</h2>
          <p className="mt-1 text-sm text-slate-500">
            Priority-ordered rules set days, lifetime, or no warranty. Lifetime never stores a fake
            expiry date.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold hover:bg-slate-50"
            onClick={() => applyDryRunMutation.mutate()}
          >
            Apply to pricing…
          </button>
          <button
            type="button"
            className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white"
            onClick={openCreate}
          >
            Add rule
          </button>
        </div>
      </div>

      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <h3 className="text-sm font-bold uppercase tracking-wide text-slate-500">Test a case</h3>
        <div className="mt-3 flex flex-wrap gap-3">
          <input
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
            value={testServiceName}
            onChange={(e) => setTestServiceName(e.target.value)}
            placeholder="Service name"
          />
          <select
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
            value={testMakeId}
            onChange={(e) => setTestMakeId(e.target.value)}
          >
            <option value="">Any make</option>
            {(makesQuery.data ?? []).map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </select>
          <select
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
            value={testCategory}
            onChange={(e) => setTestCategory(e.target.value)}
          >
            {DEVICE_CATEGORIES.map((c) => (
              <option key={c.value || 'any'} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white"
            onClick={() => testMutation.mutate()}
          >
            Test
          </button>
        </div>
        {testResult ? (
          <p className="mt-3 rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-800">{testResult}</p>
        ) : null}
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
        <ul className="divide-y divide-slate-100">
          {rules.map((rule, index) => (
            <li
              key={rule.id}
              draggable
              onDragStart={() => handleDragStart(index)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragEnd={handleDragEnd}
              className={cn(
                'flex cursor-grab items-center justify-between gap-3 px-4 py-3 active:cursor-grabbing',
                dragIndex === index && 'bg-slate-50',
              )}
            >
              <div className="min-w-0">
                <div className="font-semibold text-slate-900">
                  #{rule.priority} {rule.ruleDescription}
                </div>
                <div className="text-sm text-primary">
                  {formatWarrantySummary({
                    type: rule.warrantyType,
                    days: rule.warrantyDays,
                  })}
                </div>
              </div>
              <div className="flex shrink-0 gap-2">
                <button
                  type="button"
                  className="text-sm font-semibold text-primary"
                  onClick={() => openEdit(rule)}
                >
                  Edit
                </button>
                <button
                  type="button"
                  className="text-sm font-semibold text-red-600"
                  onClick={() => deleteMutation.mutate(rule.id)}
                >
                  Delete
                </button>
              </div>
            </li>
          ))}
          {!rules.length ? (
            <li className="px-4 py-8 text-center text-sm text-slate-500">No rules yet.</li>
          ) : null}
        </ul>
      </section>

      {showForm ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 p-4">
          <form
            onSubmit={handleSubmit}
            className="w-full max-w-lg space-y-4 rounded-2xl bg-white p-6 shadow-xl"
          >
            <h3 className="text-lg font-bold">{editing ? 'Edit rule' : 'New rule'}</h3>
            <label className="block text-sm">
              <span className="font-medium">Description</span>
              <input
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
                value={form.ruleDescription ?? ''}
                onChange={(e) => setForm((c) => ({ ...c, ruleDescription: e.target.value }))}
              />
            </label>
            <label className="block text-sm">
              <span className="font-medium">Service name pattern</span>
              <input
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
                placeholder="Screen% (use % as wildcard)"
                value={form.serviceNamePattern ?? ''}
                onChange={(e) => setForm((c) => ({ ...c, serviceNamePattern: e.target.value }))}
              />
            </label>
            <WarrantyField
              type={(form.warrantyType as WarrantyType) ?? 'days'}
              days={form.warrantyDays ?? ''}
              onTypeChange={(warrantyType) =>
                setForm((c) => ({
                  ...c,
                  warrantyType,
                  warrantyDays: warrantyType === 'days' ? c.warrantyDays || 90 : 0,
                }))
              }
              onDaysChange={(days) =>
                setForm((c) => ({
                  ...c,
                  warrantyType: 'days',
                  warrantyDays: days === '' ? 0 : Number(days),
                }))
              }
            />
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold"
                onClick={() => setShowForm(false)}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
                disabled={saveMutation.isPending}
              >
                Save
              </button>
            </div>
          </form>
        </div>
      ) : null}

      {showApply && applyPreview ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
            <h3 className="text-lg font-bold">Apply rules to pricing</h3>
            <p className="mt-2 text-sm text-slate-600">
              Would update {applyPreview.wouldUpdate} rows missing an explicit warranty.
            </p>
            <ul className="mt-3 max-h-40 space-y-1 overflow-y-auto text-sm">
              {applyPreview.preview.map((p) => (
                <li key={p.ruleDescription}>
                  {p.ruleDescription}: {p.count}
                </li>
              ))}
            </ul>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold"
                onClick={() => setShowApply(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white"
                onClick={() => applyMutation.mutate()}
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
